import React, { useState, useRef } from "react";
import toast from "react-hot-toast";
import { BoxSelect, OctagonX, Target, Mouse, Trash2, Star } from "lucide-react";

export const GROUP_PALETTE = [
    { solid: '#6366f1', bg: 'rgba(99,102,241,0.3)' },
    { solid: '#ec4899', bg: 'rgba(236,72,153,0.3)' },
    { solid: '#10b981', bg: 'rgba(16,185,129,0.3)' },
    { solid: '#f59e0b', bg: 'rgba(245,158,11,0.3)' },
    { solid: '#06b6d4', bg: 'rgba(6,182,212,0.3)' },
    { solid: '#8b5cf6', bg: 'rgba(139,92,246,0.3)' },
];

export const rectsOverlap = (a, b) =>
    a && b &&
    a.x < b.x + b.width && a.x + a.width > b.x &&
    a.y < b.y + b.height && a.y + a.height > b.y;

const DrawAreaModal = ({
    isOpen,
    onClose,
    previewUrl,
    input,
    restrictedAreas, setRestrictedAreas,
    preSelectedAreas, setPreSelectedAreas,
}) => {
    const [drawMode, setDrawMode] = useState('defect');
    const [drawStep, setDrawStep] = useState('long');
    const [pendingLongEnd, setPendingLongEnd] = useState(null);
    const [strapConfig, setStrapConfig] = useState({ width: 26, buckleWidth: 26, shortLength: 100, longLength: 150 });
    const [isDrawingArea, setIsDrawingArea] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
    const [draggingArea, setDraggingArea] = useState(null);
    const [drawImgAspect, setDrawImgAspect] = useState(1);
    const drawRef = useRef(null);
    const drawImgRef = useRef(null);

    if (!isOpen) return null;

    const checkCollision = (rect, groupIdx, endType) => {
        if (!rect) return { restricted: false, overlap: false, any: false };
        const collidesRestricted = restrictedAreas.some(res => rectsOverlap(rect, res));
        let collidesOverlap = false;
        for (let i = 0; i < preSelectedAreas.length; i++) {
            const g = preSelectedAreas[i];
            if (i === groupIdx) {
                if (endType === 'long' && g.shortEnd && rectsOverlap(rect, g.shortEnd)) collidesOverlap = true;
                else if (endType === 'short' && g.longEnd && rectsOverlap(rect, g.longEnd)) collidesOverlap = true;
            } else {
                if ((g.longEnd && rectsOverlap(rect, g.longEnd)) || (g.shortEnd && rectsOverlap(rect, g.shortEnd))) collidesOverlap = true;
            }
        }
        return { restricted: collidesRestricted, overlap: collidesOverlap, any: collidesRestricted || collidesOverlap };
    };

    const getMousePos = (e) => {
        const rect = drawRef.current.getBoundingClientRect();
        return { x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height };
    };

    const handleDeleteRestricted = (index, e) => {
        e.stopPropagation();
        const newAreas = [...restrictedAreas];
        newAreas.splice(index, 1);
        setRestrictedAreas(newAreas);
    };

    const handleDeletePreSelected = (index, e) => {
        e.stopPropagation();
        const newAreas = [...preSelectedAreas];
        newAreas.splice(index, 1);
        newAreas.forEach((a, i) => { a.groupId = `group_${i + 1}`; });
        if (newAreas.length > 0 && !newAreas.some(a => a.isDefault)) newAreas[0].isDefault = true;
        setPreSelectedAreas(newAreas);
        if (drawStep === 'short') { setPendingLongEnd(null); setDrawStep('long'); }
    };

    const handleSetDefaultPreSelected = (index, e) => {
        e.stopPropagation();
        setPreSelectedAreas(preSelectedAreas.map((area, i) => ({ ...area, isDefault: i === index })));
    };

    const handleMouseDown = (e) => {
        if (drawMode === 'preselected' && (!input.width || !input.height)) {
            toast.error("Please enter leather Width and Height (CM) before drawing Pre-select zones!");
            return;
        }
        setIsDrawingArea(true);
        const pos = getMousePos(e);
        setStartPos(pos);
        setCurrentPos(pos);
    };

    const handleMouseMove = (e) => {
        if (draggingArea) {
            const pos = getMousePos(e);
            const dx = pos.x - draggingArea.startMouse.x;
            const dy = pos.y - draggingArea.startMouse.y;
            const { groupIdx, endType, startArea } = draggingArea;
            const newX = Math.max(0, Math.min(1 - startArea.width, startArea.x + dx));
            const newY = Math.max(0, Math.min(1 - startArea.height, startArea.y + dy));
            setPreSelectedAreas(prev => prev.map((g, i) => i !== groupIdx ? g : { ...g, [endType + 'End']: { ...g[endType + 'End'], x: newX, y: newY } }));
            return;
        }
        if (!isDrawingArea) return;
        setCurrentPos(getMousePos(e));
    };

    const handleMouseUp = () => {
        if (draggingArea) {
            const { groupIdx, endType } = draggingArea;
            const group = preSelectedAreas[groupIdx];
            const rect = endType === 'long' ? group.longEnd : group.shortEnd;
            const collision = checkCollision(rect, groupIdx, endType);
            if (collision.restricted) toast.error(`Warning: G${groupIdx + 1} ${endType.toUpperCase()} collides with a restricted/defect area!`);
            else if (collision.overlap) toast.error(`Warning: G${groupIdx + 1} ${endType.toUpperCase()} overlaps with another zone!`);
            setDraggingArea(null);
            return;
        }
        if (!isDrawingArea) return;
        setIsDrawingArea(false);

        if (drawMode === 'preselected') {
            const leatherWidthMm = parseFloat(input.width) * 10;
            const imgDisplayHeightMm = leatherWidthMm * drawImgAspect;
            const widthRatio       = strapConfig.width / leatherWidthMm;
            const buckleWidthRatio = (strapConfig.buckleWidth || strapConfig.width) / leatherWidthMm;
            const longRatio        = strapConfig.longLength / imgDisplayHeightMm;
            const shortRatio       = strapConfig.shortLength / imgDisplayHeightMm;

            if (drawStep === 'long') {
                let newX = Math.max(0, Math.min(1 - widthRatio, currentPos.x - widthRatio / 2));
                let newY = Math.max(0, Math.min(1 - longRatio,  currentPos.y - longRatio / 2));
                const newLongEnd = { x: newX, y: newY, width: widthRatio, height: longRatio };
                const collision = checkCollision(newLongEnd, preSelectedAreas.length, 'long');
                if (collision.restricted) toast.error("Warning: The Long end collides with a restricted/defect area!");
                else if (collision.overlap) toast.error("Warning: The Long end overlaps with an existing zone!");
                setPendingLongEnd(newLongEnd);
                setDrawStep('short');
            } else {
                let newX = Math.max(0, Math.min(1 - buckleWidthRatio, currentPos.x - buckleWidthRatio / 2));
                let newY = Math.max(0, Math.min(1 - shortRatio,       currentPos.y - shortRatio / 2));
                const shortEnd = { x: newX, y: newY, width: buckleWidthRatio, height: shortRatio };
                if (rectsOverlap(pendingLongEnd, shortEnd)) {
                    toast.error("Short end and Long end are overlapping! Please place them separately.");
                    setPendingLongEnd(null); setDrawStep('long'); return;
                }
                const collision = checkCollision(shortEnd, preSelectedAreas.length, 'short');
                if (collision.restricted) toast.error("Warning: The Short end collides with a restricted/defect area!");
                else if (collision.overlap) toast.error("Warning: The Short end overlaps with an existing zone!");
                const groupNum = preSelectedAreas.length + 1;
                setPreSelectedAreas([...preSelectedAreas, {
                    groupId: `group_${groupNum}`,
                    uniqueAreaId: `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
                    isDefault: preSelectedAreas.length === 0,
                    isUsed: false,
                    longEnd: pendingLongEnd,
                    shortEnd,
                }]);
                setPendingLongEnd(null); setDrawStep('long');
            }
        } else {
            const w = Math.abs(currentPos.x - startPos.x);
            const h = Math.abs(currentPos.y - startPos.y);
            if (w > 0.01 && h > 0.01)
                setRestrictedAreas([...restrictedAreas, { x: Math.min(startPos.x, currentPos.x), y: Math.min(startPos.y, currentPos.y), width: w, height: h }]);
        }
    };

    const getPreviewDrawingStyle = () => {
        if (drawMode === 'preselected') {
            const leatherWidthMm = parseFloat(input.width) * 10;
            const imgDisplayHeightMm = leatherWidthMm * drawImgAspect;
            const isLongStep  = drawStep === 'long';
            const widthRatio  = (isLongStep ? strapConfig.width : (strapConfig.buckleWidth || strapConfig.width)) / leatherWidthMm;
            const heightRatio = (isLongStep ? strapConfig.longLength : strapConfig.shortLength) / imgDisplayHeightMm;
            const newX = Math.max(0, Math.min(1 - widthRatio,  currentPos.x - widthRatio / 2));
            const newY = Math.max(0, Math.min(1 - heightRatio, currentPos.y - heightRatio / 2));
            const c = GROUP_PALETTE[preSelectedAreas.length % GROUP_PALETTE.length];
            return { left: `${newX * 100}%`, top: `${newY * 100}%`, width: `${widthRatio * 100}%`, height: `${heightRatio * 100}%`, backgroundColor: c.bg, border: `2px dashed ${c.solid}` };
        }
        return { left: `${Math.min(startPos.x, currentPos.x) * 100}%`, top: `${Math.min(startPos.y, currentPos.y) * 100}%`, width: `${Math.abs(currentPos.x - startPos.x) * 100}%`, height: `${Math.abs(currentPos.y - startPos.y) * 100}%`, backgroundColor: 'rgba(220, 53, 69, 0.2)', border: '2px dashed #dc3545' };
    };

    const hasRestrictedCollision = preSelectedAreas.some((g, i) =>
        (g.longEnd && checkCollision(g.longEnd, i, 'long').restricted) || (g.shortEnd && checkCollision(g.shortEnd, i, 'short').restricted)
    );
    const hasOverlapCollision = preSelectedAreas.some((g, i) =>
        (g.longEnd && checkCollision(g.longEnd, i, 'long').overlap) || (g.shortEnd && checkCollision(g.shortEnd, i, 'short').overlap)
    );
    const hasAnyCollision = hasRestrictedCollision || hasOverlapCollision;

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 99999, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', flexDirection: 'column', backdropFilter: 'blur(8px)' }}>
            <div className="d-flex justify-content-between align-items-center p-3 px-4 shadow-sm" style={{ backgroundColor: '#1A110B', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="text-white d-flex align-items-center gap-4">
                    <div>
                        <h5 className="mb-1 fw-bold d-flex align-items-center"><BoxSelect size={24} className="text-warning me-2" /> Mark Areas</h5>
                        <span className="small text-muted" style={{ letterSpacing: '0.5px' }}>Click and drag to define zones.</span>
                    </div>
                    <div className="btn-group bg-dark border border-secondary rounded p-1">
                        <button onClick={() => { setDrawMode('defect'); setDrawStep('long'); setPendingLongEnd(null); }} className={`btn btn-sm fw-bold d-flex align-items-center gap-1 ${drawMode === 'defect' ? 'btn-danger text-white' : 'btn-dark text-muted'}`}><OctagonX size={14} /> Defect (Red)</button>
                        <button onClick={() => { setDrawMode('preselected'); setDrawStep('long'); setPendingLongEnd(null); }} className={`btn btn-sm fw-bold d-flex align-items-center gap-1 ${drawMode === 'preselected' ? 'btn-success text-white' : 'btn-dark text-muted'}`}><Target size={14} /> Pre-select (Green)</button>
                    </div>
                    {drawMode === 'preselected' && (
                        <div className="d-flex align-items-center gap-2 ms-2">
                            <span className="text-muted small" style={{ whiteSpace: 'nowrap' }}>Strap (mm):</span>
                            <div className="d-flex align-items-center gap-1">
                                <label className="text-muted" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>W</label>
                                <input type="number" min="1" max="500" value={strapConfig.width} onChange={e => { const v = parseFloat(e.target.value) || 26; setStrapConfig(s => ({ ...s, width: v })); }} className="form-control form-control-sm text-white text-center" style={{ width: '60px', backgroundColor: '#2a1a0e', border: '1px solid #555', borderRadius: '6px', fontSize: '12px' }} />
                            </div>
                            <div className="d-flex align-items-center gap-1">
                                <label className="text-muted" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>Buckle</label>
                                <input type="number" min="1" max="500" value={strapConfig.buckleWidth || strapConfig.width} onChange={e => { const v = parseFloat(e.target.value) || 26; setStrapConfig(s => ({ ...s, buckleWidth: v })); }} className="form-control form-control-sm text-white text-center" style={{ width: '60px', backgroundColor: '#2a1a0e', border: '1px solid #555', borderRadius: '6px', fontSize: '12px' }} />
                            </div>
                            <div className="d-flex align-items-center gap-1">
                                <label className="text-muted" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>Long</label>
                                <input type="number" min="1" max="2000" value={strapConfig.longLength} onChange={e => { const v = parseFloat(e.target.value) || 150; setStrapConfig(s => ({ ...s, longLength: v })); }} className="form-control form-control-sm text-white text-center" style={{ width: '64px', backgroundColor: '#2a1a0e', border: '1px solid #555', borderRadius: '6px', fontSize: '12px' }} />
                            </div>
                            <div className="d-flex align-items-center gap-1">
                                <label className="text-muted" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>Short</label>
                                <input type="number" min="1" max="2000" value={strapConfig.shortLength} onChange={e => { const v = parseFloat(e.target.value) || 100; setStrapConfig(s => ({ ...s, shortLength: v })); }} className="form-control form-control-sm text-white text-center" style={{ width: '64px', backgroundColor: '#2a1a0e', border: '1px solid #555', borderRadius: '6px', fontSize: '12px' }} />
                            </div>
                        </div>
                    )}
                </div>
                <div className="d-flex gap-3 align-items-center">
                    {drawMode === 'preselected' && (
                        <span className={`badge px-3 py-2 fw-bold shadow-sm ${drawStep === 'long' ? 'bg-warning text-dark' : 'bg-info text-white'}`}>
                            {drawStep === 'long' ? (pendingLongEnd ? '⚠️ Prev group pending' : '① Click: Long End') : '② Click: Short End'}
                        </span>
                    )}
                    <span className="badge bg-danger text-white px-3 py-2 fw-bold shadow-sm">{restrictedAreas.length} Defects</span>
                    <span className="badge bg-success text-white px-3 py-2 fw-bold shadow-sm">{preSelectedAreas.length} Groups</span>
                    {hasRestrictedCollision && <span className="badge px-3 py-2 fw-bold shadow-sm ms-2" style={{ backgroundColor: '#dc3545', color: '#fff', animation: 'pulse 1s infinite' }}>⚠ COLLIDES WITH RESTRICTED AREA</span>}
                    {hasOverlapCollision && <span className="badge px-3 py-2 fw-bold shadow-sm ms-2" style={{ backgroundColor: '#dc3545', color: '#fff', animation: 'pulse 1s infinite' }}>⚠ OVERLAP DETECTED</span>}
                    <button onClick={() => { setRestrictedAreas([]); setPreSelectedAreas([]); }} className="btn btn-outline-light btn-sm fw-bold px-3 py-2 ms-3" style={{ borderRadius: '8px' }}>CLEAR ALL</button>
                    <button
                        onClick={() => { if (hasAnyCollision) { toast.error("Cannot complete! Some zones overlap or collide with restricted areas."); return; } onClose(); }}
                        className="btn btn-sm fw-bold px-4 py-2"
                        style={{ backgroundColor: hasAnyCollision ? '#dc3545' : '#C5A075', color: '#FFF', borderRadius: '8px', border: 'none', cursor: hasAnyCollision ? 'not-allowed' : 'pointer', opacity: hasAnyCollision ? 0.7 : 1 }}
                    >DONE</button>
                </div>
            </div>

            <div className="flex-grow-1 d-flex justify-content-center align-items-center p-4 overflow-hidden position-relative">
                {restrictedAreas.length === 0 && preSelectedAreas.length === 0 && !isDrawingArea && (
                    <div className="position-absolute text-white fw-bold text-center" style={{ top: '10%', zIndex: 10, opacity: 0.5, pointerEvents: 'none', letterSpacing: '2px' }}><Mouse size={24} style={{ display: 'inline', marginRight: '8px' }} /><br />CLICK &amp; DRAG TO DRAW</div>
                )}
                <div ref={drawRef} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} className="position-relative shadow-lg rounded" style={{ display: 'inline-block', cursor: draggingArea ? 'grabbing' : 'crosshair', border: '3px solid #C5A075', backgroundColor: '#111', overflow: 'hidden' }}>
                    <img ref={drawImgRef} src={previewUrl} alt="Draw" decoding="async" style={{ maxHeight: '75vh', maxWidth: '85vw', display: 'block', userSelect: 'none', opacity: 0.9 }} draggable="false" crossOrigin="anonymous"
                        onLoad={() => { if (drawImgRef.current) setDrawImgAspect(drawImgRef.current.naturalHeight / drawImgRef.current.naturalWidth); }} />

                    {restrictedAreas.map((rect, idx) => (
                        <div key={`res-${idx}`} style={{ position: 'absolute', left: `${rect.x * 100}%`, top: `${rect.y * 100}%`, width: `${rect.width * 100}%`, height: `${rect.height * 100}%`, backgroundColor: 'rgba(220, 53, 69, 0.3)', border: '2px dashed #dc3545', display: 'flex', justifyContent: 'center', alignItems: 'center', pointerEvents: 'none' }}>
                            <div className="bg-danger text-white rounded px-2 py-1 shadow d-flex flex-column align-items-center" style={{ fontSize: '11px', fontWeight: 'bold', pointerEvents: 'auto' }} onMouseDown={e => e.stopPropagation()}>
                                DEFECT
                                <button onClick={(e) => handleDeleteRestricted(idx, e)} className="btn btn-sm btn-light py-0 px-1 mt-1" style={{ fontSize: '10px' }} title="Delete"><Trash2 size={12} className="text-danger" /></button>
                            </div>
                        </div>
                    ))}

                    {preSelectedAreas.map((group, idx) => {
                        const c = GROUP_PALETTE[idx % GROUP_PALETTE.length];
                        const isDraggingThis = draggingArea?.groupIdx === idx;
                        const longCollision = checkCollision(group.longEnd, idx, 'long');
                        const shortCollision = checkCollision(group.shortEnd, idx, 'short');
                        return (
                            <React.Fragment key={`pre-group-${idx}`}>
                                <div onMouseDown={(e) => { e.stopPropagation(); setDraggingArea({ groupIdx: idx, endType: 'long', startMouse: getMousePos(e), startArea: { ...group.longEnd } }); }}
                                    style={{ position: 'absolute', left: `${group.longEnd.x * 100}%`, top: `${group.longEnd.y * 100}%`, width: `${group.longEnd.width * 100}%`, height: `${group.longEnd.height * 100}%`, backgroundColor: longCollision.any ? 'rgba(220,53,69,0.25)' : c.bg, border: `2px solid ${longCollision.any ? '#dc3545' : c.solid}`, display: 'flex', justifyContent: 'center', alignItems: 'center', pointerEvents: 'auto', cursor: isDraggingThis && draggingArea?.endType === 'long' ? 'grabbing' : 'grab', userSelect: 'none' }}>
                                    <div style={{ backgroundColor: longCollision.any ? '#dc3545' : c.solid, color: '#fff', borderRadius: '4px', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                        {longCollision.any ? `⚠ G${idx+1} LONG` : (group.isDefault ? `⭐ G${idx+1} LONG` : `G${idx+1} LONG`)}
                                        <div className="d-flex gap-1 mt-1">
                                            {!group.isDefault && <button onClick={(e) => handleSetDefaultPreSelected(idx, e)} onMouseDown={e => e.stopPropagation()} className="btn btn-sm btn-light py-0 px-1" style={{ fontSize: '10px' }} title="Set Default"><Star size={12} className="text-warning" fill="currentColor" /></button>}
                                            <button onClick={(e) => handleDeletePreSelected(idx, e)} onMouseDown={e => e.stopPropagation()} className="btn btn-sm btn-light py-0 px-1" style={{ fontSize: '10px' }} title="Delete"><Trash2 size={12} className="text-danger" /></button>
                                        </div>
                                    </div>
                                </div>
                                <div onMouseDown={(e) => { e.stopPropagation(); setDraggingArea({ groupIdx: idx, endType: 'short', startMouse: getMousePos(e), startArea: { ...group.shortEnd } }); }}
                                    style={{ position: 'absolute', left: `${group.shortEnd.x * 100}%`, top: `${group.shortEnd.y * 100}%`, width: `${group.shortEnd.width * 100}%`, height: `${group.shortEnd.height * 100}%`, backgroundColor: shortCollision.any ? 'rgba(220,53,69,0.25)' : c.bg, border: `2px dashed ${shortCollision.any ? '#dc3545' : c.solid}`, display: 'flex', justifyContent: 'center', alignItems: 'center', pointerEvents: 'auto', cursor: isDraggingThis && draggingArea?.endType === 'short' ? 'grabbing' : 'grab', userSelect: 'none' }}>
                                    <div style={{ backgroundColor: shortCollision.any ? '#dc3545' : c.solid, color: '#fff', borderRadius: '4px', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold' }}>
                                        {shortCollision.any ? `⚠ G${idx+1} SHORT` : `G${idx+1} SHORT`}
                                    </div>
                                </div>
                            </React.Fragment>
                        );
                    })}

                    {pendingLongEnd && (() => { const c = GROUP_PALETTE[preSelectedAreas.length % GROUP_PALETTE.length]; return (
                        <div style={{ position: 'absolute', left: `${pendingLongEnd.x * 100}%`, top: `${pendingLongEnd.y * 100}%`, width: `${pendingLongEnd.width * 100}%`, height: `${pendingLongEnd.height * 100}%`, backgroundColor: c.bg, border: `2px solid ${c.solid}`, display: 'flex', justifyContent: 'center', alignItems: 'center', pointerEvents: 'none', animation: 'pulse 1.5s infinite' }}>
                            <div style={{ backgroundColor: c.solid, color: '#fff', borderRadius: '4px', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold' }}>LONG → click SHORT...</div>
                        </div>
                    ); })()}

                    {isDrawingArea && <div style={{ position: 'absolute', pointerEvents: 'none', ...getPreviewDrawingStyle() }} />}
                </div>
            </div>
        </div>
    );
};

export default DrawAreaModal;
